<template>
  <div class="display-config-page">
    <el-card shadow="hover">
      <div slot="header" class="card-header">
        <span>取餐大屏配置</span>
        <el-button type="primary" size="small" :loading="saving" @click="save">保存配置</el-button>
      </div>

      <el-form label-width="150px" size="small" class="form">
        <el-form-item label="选择门店">
          <el-select v-model="storeId" filterable placeholder="选择门店" style="width: 320px" @change="loadConfig">
            <el-option label="默认（全部门店共用 store_id=0）" :value="0" />
            <el-option
              v-for="s in stores"
              :key="s.id"
              :label="s.name"
              :value="Number(s.id)"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="大屏访问地址">
          <div class="url-row">
            <el-input :value="displayUrl" readonly />
            <el-button @click="copyUrl">复制</el-button>
            <el-button type="success" plain @click="openDisplay">全屏打开</el-button>
          </div>
          <div class="hint">推荐地址（经 /api，不被门户拦截）。门店电视 F11 全屏；每 3 秒刷新订单。</div>
        </el-form-item>

        <el-divider content-position="left">左侧品牌照片区</el-divider>

        <el-form-item label="品牌标题">
          <el-input v-model="form.brand_title" placeholder="留空则不显示文字" clearable style="width: 280px" />
          <div class="hint">留空后大屏左上角不再显示文字（可只保留角标 LOGO）</div>
        </el-form-item>

        <el-form-item label="角标 LOGO">
          <div class="logo-row">
            <img v-if="form.logo_url" :src="form.logo_url" class="logo-preview" alt="logo" />
            <el-upload :show-file-list="false" :http-request="uploadLogo" accept="image/*">
              <el-button size="small">上传</el-button>
            </el-upload>
            <el-button v-if="form.logo_url" size="small" type="text" @click="form.logo_url = ''">清除</el-button>
          </div>
        </el-form-item>

        <el-form-item label="轮播图片">
          <div class="brand-images">
            <div v-for="(url, idx) in form.brand_images" :key="idx" class="brand-item">
              <img :src="url" alt="" />
              <div class="brand-ops">
                <el-button type="text" size="mini" :disabled="idx === 0" @click="moveImage(idx, -1)">上移</el-button>
                <el-button
                  type="text"
                  size="mini"
                  :disabled="idx === form.brand_images.length - 1"
                  @click="moveImage(idx, 1)"
                >下移</el-button>
                <el-button type="text" size="mini" class="danger" @click="removeImage(idx)">删除</el-button>
              </div>
            </div>
            <el-upload
              class="brand-uploader"
              :show-file-list="false"
              :http-request="uploadBrandImage"
              accept="image/*"
            >
              <el-button size="small" icon="el-icon-plus">添加图片</el-button>
            </el-upload>
          </div>
          <div class="hint">任意比例均可上传；大屏左侧会自动裁剪铺满（object-fit: cover），无需预先裁切。</div>
        </el-form-item>

        <el-form-item label="轮播间隔(秒)">
          <el-input-number v-model="form.brand_carousel_interval" :min="2" :max="60" />
        </el-form-item>

        <el-divider content-position="left">右侧订单区</el-divider>

        <el-form-item label="每区最多显示">
          <el-input-number v-model="form.page_size" :min="1" :max="18" />
          <span class="hint inline">默认 9；超出进入等待队列，前面完成后自动补位（不再翻页）</span>
        </el-form-item>
        <el-form-item label="翻页间隔(秒)" style="display:none">
          <el-input-number v-model="form.page_interval" :min="2" :max="60" />
        </el-form-item>
        <el-form-item label="显示商品简要">
          <el-switch v-model="form.show_items_summary" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="显示下单时间">
          <el-switch v-model="form.show_order_time" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="卡片阴影">
          <el-radio-group v-model="form.card_style">
            <el-radio label="simple">简约</el-radio>
            <el-radio label="standard">标准</el-radio>
            <el-radio label="rich">浓郁</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-divider content-position="left">叫号与业务</el-divider>

        <el-form-item label="收银台取餐码前缀">
          <el-input v-model="form.code_prefix" maxlength="5" style="width: 120px" placeholder="A" />
          <span class="hint inline">默认 A，收银台订单单独递增（如 A001、A002）</span>
        </el-form-item>
        <el-form-item label="小程序取餐码前缀">
          <el-input v-model="form.mini_code_prefix" maxlength="5" style="width: 120px" placeholder="X" />
          <span class="hint inline">默认 X，小程序订单单独递增（如 X001、X002）；两类都会上取餐屏</span>
        </el-form-item>
        <el-form-item label="语音叫号">
          <el-switch v-model="form.voice_enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="自动完成制作(分钟)">
          <el-input-number v-model="form.auto_prepare_minutes" :min="0" :max="240" />
          <span class="hint inline">制作中订单自下单起超时自动「完成制作」进入请取餐；默认 10；0 关闭</span>
        </el-form-item>
        <el-form-item label="自动完成取餐(分钟)">
          <el-input-number v-model="form.auto_complete_minutes" :min="0" :max="240" />
          <span class="hint inline">请取餐超时自动确认完成；0 关闭</span>
        </el-form-item>
        <el-form-item label="外卖也上大屏">
          <el-switch v-model="form.show_delivery" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="hover" style="margin-top: 16px">
      <div slot="header" class="card-header">
        <span>厨显快捷操作</span>
        <el-button size="small" :loading="loadingOrders" @click="loadOrders">刷新</el-button>
      </div>
      <el-tabs v-model="kitchenTab">
        <el-tab-pane :label="'制作中 (' + preparing.length + ')'" name="preparing">
          <el-table :data="preparing" border size="small">
            <el-table-column prop="pickup_code" label="取餐码" width="100" />
            <el-table-column prop="items_summary" label="商品" min-width="220" />
            <el-table-column prop="phone_tail" label="尾号" width="90" />
            <el-table-column label="下单时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="140" align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="mini" @click="completePrepare(scope.row)">完成制作</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane :label="'请取餐 (' + ready.length + ')'" name="ready">
          <el-table :data="ready" border size="small">
            <el-table-column prop="pickup_code" label="取餐码" width="100" />
            <el-table-column prop="items_summary" label="商品" min-width="220" />
            <el-table-column label="下单时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="140" align="center">
              <template slot-scope="scope">
                <el-button type="warning" size="mini" @click="confirmPickup(scope.row)">确认取餐</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import { routeTabMixin } from '../utils/routeTab'

export default {
  name: 'DisplayConfig',
  mixins: [
    routeTabMixin({
      dataKey: 'kitchenTab',
      queryKey: 'panel',
      allowed: ['preparing', 'ready']
    })
  ],
  data() {
    return {
      storeId: 0,
      stores: [],
      saving: false,
      loadingOrders: false,
      kitchenTab: 'preparing',
      preparing: [],
      ready: [],
      form: {
        logo_url: '',
        brand_title: '',
        brand_images: [],
        brand_carousel_interval: 5,
        page_size: 9,
        page_interval: 5,
        show_items_summary: 1,
        show_order_time: 1,
        card_style: 'standard',
        code_prefix: 'A',
        mini_code_prefix: 'X',
        voice_enabled: 0,
        auto_prepare_minutes: 10,
        auto_complete_minutes: 30,
        show_delivery: 0
      },
      pollTimer: null
    }
  },
  computed: {
    displayUrl() {
      const origin = window.location.origin
      return `${origin}/api/display/screen?store_id=${this.storeId || 0}`
    }
  },
  mounted() {
    this.loadStores()
    this.loadConfig()
    this.loadOrders()
    this.pollTimer = setInterval(() => this.loadOrders(), 5000)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    formatTime(v) {
      if (!v) return '-'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    async loadStores() {
      try {
        const resp = await this.$api.get('/stores/admin/all')
        if (resp && resp.code === 200) this.stores = resp.data || []
      } catch (e) {
        /* ignore */
      }
    },
    normalizeBrandImages(raw) {
      let list = raw
      if (list == null || list === '') return []
      if (typeof list === 'string') {
        try {
          list = JSON.parse(list)
          if (typeof list === 'string') {
            try {
              list = JSON.parse(list)
            } catch (_e2) {
              list = [list]
            }
          }
        } catch (_e) {
          return list.trim() ? [list.trim()] : []
        }
      }
      if (!Array.isArray(list)) return []
      return list
        .map((x) => {
          if (typeof x === 'string') return x.trim()
          if (x && x.url) return String(x.url).trim()
          return ''
        })
        .filter(Boolean)
    },
    applyConfigData(data) {
      if (!data || typeof data !== 'object') return
      const next = Object.assign({}, this.form, data)
      next.brand_images = this.normalizeBrandImages(data.brand_images)
      next.brand_title = data.brand_title != null ? String(data.brand_title) : ''
      this.form = next
    },
    async loadConfig() {
      try {
        const resp = await this.$api.get('/display/config', { params: { store_id: this.storeId } })
        if (resp && resp.code === 200 && resp.data) {
          this.applyConfigData(resp.data)
        }
      } catch (e) {
        this.$message.error('加载大屏配置失败')
      }
    },
    async save(opts = {}) {
      const quiet = !!(opts && opts.quiet)
      this.saving = true
      try {
        const payload = Object.assign({}, this.form, {
          store_id: this.storeId,
          brand_images: this.normalizeBrandImages(this.form.brand_images)
        })
        // 去掉前端临时字段，避免干扰
        delete payload.id
        delete payload.created_at
        delete payload.updated_at
        const resp = await this.$api.post('/display/config', payload)
        if (resp && resp.code === 200) {
          if (!quiet) this.$message.success('已保存')
          if (resp.data) this.applyConfigData(resp.data)
          return true
        }
        this.$message.error((resp && resp.message) || '保存失败')
        return false
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.apiBody && e.apiBody.message) ||
          (e && e.message) ||
          '保存失败'
        this.$message.error(msg)
        return false
      } finally {
        this.saving = false
      }
    },
    async uploadImageFile(file) {
      if (!file) throw new Error('未选择文件')
      const fd = new FormData()
      // 与后端 multer 约定字段名 file（兼容旧 image）
      fd.append('file', file)
      const resp = await this.$api.post('/display/upload', fd, {
        // 不要手动写 multipart boundary，交给浏览器自动带上
        headers: { 'Content-Type': undefined },
        transformRequest: [
          (data, headers) => {
            if (headers && typeof headers.delete === 'function') {
              headers.delete('Content-Type')
            } else if (headers) {
              delete headers['Content-Type']
            }
            return data
          }
        ]
      })
      const url =
        (resp && resp.data && (resp.data.url || resp.data.path)) ||
        (resp && resp.url) ||
        ''
      if (!url) {
        const msg = (resp && resp.message) || '上传失败：未返回图片地址'
        throw new Error(msg)
      }
      return url
    },
    async uploadLogo(req) {
      try {
        const url = await this.uploadImageFile(req.file)
        this.form.logo_url = url
        this.$message.success('LOGO 已上传')
        req.onSuccess && req.onSuccess()
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '上传失败'
        this.$message.error(msg)
        req.onError && req.onError(e)
      }
    },
    async uploadBrandImage(req) {
      try {
        const url = await this.uploadImageFile(req.file)
        const list = this.normalizeBrandImages(this.form.brand_images)
        list.push(url)
        this.$set(this.form, 'brand_images', list)
        this.$message.success('已添加轮播图')
        req.onSuccess && req.onSuccess()
        // 上传后立即落库，避免「看起来加了、保存后消失」
        await this.save({ quiet: true })
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '上传失败'
        this.$message.error(msg)
        req.onError && req.onError(e)
      }
    },
    async removeImage(idx) {
      const list = this.normalizeBrandImages(this.form.brand_images)
      list.splice(idx, 1)
      this.$set(this.form, 'brand_images', list)
      await this.save({ quiet: true })
    },
    moveImage(idx, delta) {
      const arr = this.normalizeBrandImages(this.form.brand_images)
      const j = idx + delta
      if (j < 0 || j >= arr.length) return
      const t = arr[idx]
      arr[idx] = arr[j]
      arr[j] = t
      this.$set(this.form, 'brand_images', arr)
    },
    copyUrl() {
      const text = this.displayUrl
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('已复制'))
        return
      }
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      this.$message.success('已复制')
    },
    openDisplay() {
      window.open(this.displayUrl, '_blank')
    },
    async loadOrders() {
      this.loadingOrders = true
      try {
        const resp = await this.$api.get('/display/orders', { params: { store_id: this.storeId } })
        if (resp && resp.code === 200 && resp.data) {
          this.preparing = resp.data.preparing || []
          this.ready = resp.data.ready || []
        }
      } catch (e) {
        /* ignore */
      } finally {
        this.loadingOrders = false
      }
    },
    async completePrepare(row) {
      try {
        const resp = await this.$api.post('/display/complete-prepare', { order_id: row.id })
        if (resp && resp.code === 200) {
          this.$message.success('已完成制作')
          this.loadOrders()
        } else this.$message.error((resp && resp.message) || '操作失败')
      } catch (e) {
        this.$message.error((e && e.response && e.response.data && e.response.data.message) || '操作失败')
      }
    },
    async confirmPickup(row) {
      try {
        const resp = await this.$api.post('/display/confirm-pickup', { order_id: row.id })
        if (resp && resp.code === 200) {
          this.$message.success('已确认取餐')
          this.loadOrders()
        } else this.$message.error((resp && resp.message) || '操作失败')
      } catch (e) {
        this.$message.error((e && e.response && e.response.data && e.response.data.message) || '操作失败')
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.url-row {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 760px;
}
.hint {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}
.hint.inline {
  margin-left: 12px;
}
.logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.logo-preview {
  height: 48px;
  max-width: 160px;
  object-fit: contain;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 4px;
  background: #f5f7fa;
}
.brand-images {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}
.brand-item {
  width: 140px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}
.brand-item img {
  width: 140px;
  height: 90px;
  object-fit: cover;
  display: block;
}
.brand-ops {
  display: flex;
  justify-content: space-around;
  padding: 2px 0;
}
.brand-ops .danger {
  color: #f56c6c;
}
</style>
